import { ISessionResponse } from "../../Types"
import { logger } from "../../App/Logger"
import { CompanionConnectError, CompanionConnectPhase, CompanionConnectState, CompanionResolveReason } from "./Params"

// How long the whole automatic attempt -- resolve the advertised service, then log in
// to the resolved peer -- may take before the dialog gives up and offers the manual
// form instead. Deliberately covers BOTH steps: during the #1279 investigation a stale
// advertisement resolved to a dead link-local address and the pairing request only
// failed once the network stack gave up, far longer than anyone waits on a boat.
export const companionConnectTimeoutMs = 4000;

// The manual form's own budget, deliberately longer than the automatic one and kept
// separate from it. The automatic deadline is short because failing fast is the point:
// it exists to hand the user the manual form. That form is the last route left, its
// address was chosen by the user rather than guessed from a stale advertisement, and a
// primary on a congested boat network can take several seconds to answer -- so cutting
// it off at four seconds would abandon a pairing that was about to succeed.
//
// #1388: it previously had NO deadline at all, so the one route a user reaches BECAUSE
// the automatic one failed was the one that could hang until the OS network stack gave
// up, with the button spinning and no reason ever offered.
export const companionSubmitTimeoutMs = 10000;
export const companionHandoffTimeoutMs = 10000;

const disconnectGenerations = new Map<number, number>();

export function invalidateCompanionConnect(shipId: number): void {
  disconnectGenerations.set(shipId, (disconnectGenerations.get(shipId) ?? 0) + 1);
}

// What resolving a ship's advertised service produced: an address, a reason it could
// not be resolved, or neither. The reason matters because the automatic route dies at
// resolution more often than at pairing, and the connect that follows can only report
// the generic failure it sees (#1388).
export interface CompanionResolveResult {
  address?: string;
  reason?: CompanionResolveReason;
}

export interface CompanionConnectParams {
  shipId: number;
  // Resolve the ship's advertised service. Reports the address when it has one and why
  // it does not when it doesn't (never discovered, ambiguous, or unresolvable).
  resolve: (shipId: number) => Promise<CompanionResolveResult | undefined>;
  // Pair with the primary. `attempt` is echoed to the app-local route, which re-checks
  // it before persisting so a superseded attempt cannot commit late.
  connectLocal: (shipId: number, address: string | undefined, attempt: string) => Promise<ISessionResponse | undefined>;
  cancelLocal?: (shipId: number, attempt: string) => Promise<void>;
  prepareHandoff?: (shipId: number) => Promise<string>;
  // Runs only for an attempt that is still current when its response lands.
  onSuccess: (shipId: number, destination?: string) => Promise<void>;
  onState: (state: CompanionConnectState) => void;
  // Overrides the deadline above. Exists so the timeout state can be reached
  // deterministically (the catalog renders it this way): a value of 0 or less makes
  // the deadline immediate instead of waiting on a timer, which no snapshot render
  // would ever sit still for.
  timeoutMs?: number;
  // The same, for the manual form's separate budget.
  submitTimeoutMs?: number;
  handoffTimeoutMs?: number;
}

export interface CompanionConnect {
  // Opens the dialog. `nearby` decides only whether an AUTOMATIC attempt is worth
  // making -- it never changes which dialog opens, so a row cannot change identity
  // under the user's finger.
  start: (options: { nearby: boolean }) => Promise<void>;
  submit: (address: string) => Promise<void>;
  cancel: () => Promise<void>;
  dispose: () => Promise<void>;
}

// A session response only counts as success when it actually carries a session id.
// The previous implementation checked this inline and simply fell through otherwise,
// which is how an authorized-but-session-less response became a silent no-op (#1279).
function connected(response?: ISessionResponse): boolean {
  return !!response && !!response.session && !!response.session.id;
}

// The error name the primary answers with when it refuses a companion running an
// older build (`LogbookApiError.companionVersionOutdated`, served as a 400).
const outdatedError = "companionVersionOutdated";

// Every failure the app-local connect route can name, mapped to the reason the dialog
// shows. Keyed on the error NAME rather than the status: these are all 400s, and
// `notFound`/`unauthorized` deliberately do not appear because they flatten to
// human-readable text on the wire and so cannot identify a cause (#1388).
const connectErrors: Record<string, CompanionConnectError> = {
  companionShipNotAccessible: CompanionConnectError.notAccessible,
  companionAddressInvalid: CompanionConnectError.addressInvalid,
  companionShipNotFound: CompanionConnectError.notDiscovered,
  companionShipNotClaimed: CompanionConnectError.notClaimed,
  companionShipUnresolved: CompanionConnectError.unresolved,
  companionPrimaryUnreachable: CompanionConnectError.unreachable,
  companionRefused: CompanionConnectError.refused,
  shipOwnedByThisDevice: CompanionConnectError.ownDevice,
};

// A resolve failure describes the same ground as some connect failures, so the two
// share the dialog's vocabulary rather than inventing a second one.
const resolveReasons: Record<CompanionResolveReason, CompanionConnectError> = {
  [CompanionResolveReason.notDiscovered]: CompanionConnectError.notDiscovered,
  [CompanionResolveReason.ambiguous]: CompanionConnectError.ambiguous,
  [CompanionResolveReason.unresolved]: CompanionConnectError.unresolved,
};

// The error name carried by a rejected request, when there is one.
function errorName(error: unknown): string | undefined {
  if (!error || typeof error != "object") {
    return undefined;
  }
  const name = (error as { error?: unknown }).error;
  return typeof name == "string" ? name : undefined;
}

// Whether a rejected request is the primary's version refusal. Keyed on the error
// NAME, never on the 400 alone: `shipOwnedByThisDevice` is also a 400 from this same
// route (#1279), and reporting that as "your app is out of date" would send the user
// to the App Store to fix something an update cannot touch.
export function isOutdatedError(error: unknown): boolean {
  return errorName(error) === outdatedError;
}

// The reason a rejected request identifies, or undefined when it names nothing we
// recognise -- which is what lets the resolve reason take over.
export function connectErrorReason(error: unknown): CompanionConnectError | undefined {
  const name = errorName(error);
  return name ? connectErrors[name] : undefined;
}

// Drives one companion pairing from a single dialog: spinner while the automatic route
// is attempted, then either success or the manual form carrying a reason. Every exit
// path publishes a state -- there is deliberately no branch that just returns.
export function createCompanionConnect(params: CompanionConnectParams): CompanionConnect {
  const { shipId, resolve, connectLocal, onSuccess, onState } = params;
  const timeoutMs = params.timeoutMs ?? companionConnectTimeoutMs;
  const submitTimeoutMs = params.submitTimeoutMs ?? companionSubmitTimeoutMs;
  const handoffTimeoutMs = params.handoffTimeoutMs ?? companionHandoffTimeoutMs;
  // Monotonic attempt token. Incremented on cancel and on every new attempt, so a
  // response from a superseded attempt is recognised and dropped rather than applied.
  let attemptId = 0;
  let serverAttemptId = 0;
  const workflowId = Array.from(crypto.getRandomValues(new Uint32Array(4)), (value) => value.toString(16)).join("-");
  const serverToken = (id: number) => `${workflowId}-${id}`;
  let succeeded = false;
  let cancelled = false;
  let generation = disconnectGenerations.get(shipId);
  const isCurrent = (token: number) => token === attemptId && generation === disconnectGenerations.get(shipId);
  let resolvedAddress: string | undefined;
  let resolveReason: CompanionResolveReason | undefined;
  let submittingAttempt: number | undefined;

  const emit = (state: CompanionConnectState) => onState(state);
  const cancelServer = async (token: number) => params.cancelLocal?.(shipId, serverToken(token));
  const cancelExpiredAttempt = (token: number) => {
    void cancelServer(token).catch((error) => logger.error("Failed to cancel expired companion pairing", shipId, error));
  };

  const manual = (error?: CompanionConnectError) => emit({ phase: CompanionConnectPhase.manual, address: resolvedAddress, error });

  // The most specific reason available, in precedence order: what the connect route
  // named, then what resolution reported, then the catch-all. Resolution comes second
  // because a named connect failure describes a later, more decisive step -- but it
  // comes at all because the automatic route usually dies at resolution, where the
  // connect can only report that something generic went wrong (#1388).
  const reasonFor = (error?: unknown): CompanionConnectError =>
    connectErrorReason(error)
    ?? (resolveReason ? resolveReasons[resolveReason] : undefined)
    ?? CompanionConnectError.failed;

  // Resolves once the attempt finishes or the deadline passes, whichever is first. The
  // request itself is not abortable from here, so the attempt token is what guarantees
  // a late response cannot complete the handoff behind the user's back. The budget is
  // a parameter because the automatic and manual routes deliberately differ (#1388).
  const withDeadline = async <T>(work: (finishDeadline: () => void) => Promise<T>, ms: number): Promise<{ timedOut: boolean; value?: T }> => {
    let timer: ReturnType<typeof setTimeout>;
    const deadline = ms > 0
      ? new Promise<{ timedOut: true }>((resolveDeadline) => {
          timer = setTimeout(() => resolveDeadline({ timedOut: true }), ms);
        })
      : Promise.resolve({ timedOut: true as const });
    try {
      return await Promise.race([
        work(() => clearTimeout(timer)).then((value) => ({ timedOut: false as const, value })),
        deadline,
      ]);
    } finally {
      clearTimeout(timer!);
    }
  };

  const attempt = async (address: string | undefined, attemptToken: number, finishPairingDeadline: () => void): Promise<void> => {
    let response: ISessionResponse | undefined;
    try {
      response = await connectLocal(shipId, address, serverToken(attemptToken));
    } catch (error) {
      if (!isCurrent(attemptToken)) {
        // Cancelled or superseded while in flight: drop the result entirely.
        return;
      }
      if (isOutdatedError(error)) {
        // Terminal: settle here rather than rethrowing into the callers' generic
        // catch, which would replace this with the manual form and lose the reason.
        emit({ phase: CompanionConnectPhase.outdated });
        return;
      }
      // Classified here rather than rethrown, so the reason survives instead of being
      // flattened by a caller's catch -- which is exactly how eight causes became one
      // message (#1388).
      manual(reasonFor(error));
      return;
    }
    if (!isCurrent(attemptToken)) {
      // Cancelled or superseded while in flight: drop the result entirely.
      return;
    }
    if (!connected(response)) {
      // The primary answered and issued no session. That is a refusal, not a network
      // problem, and used to be indistinguishable from an unreachable address.
      manual(CompanionConnectError.refused);
      return;
    }
    finishPairingDeadline();
    emit({ phase: CompanionConnectPhase.success, address: resolvedAddress });
    const prepared = await withDeadline(async () => params.prepareHandoff?.(shipId), handoffTimeoutMs);
    if (!isCurrent(attemptToken)) {
      return;
    }
    if (prepared.timedOut) {
      attemptId++;
      manual(CompanionConnectError.unreachable);
      cancelExpiredAttempt(attemptToken);
      return;
    }
    await onSuccess(shipId, prepared.value);
    succeeded = isCurrent(attemptToken);
  };

  return {
    async start({ nearby }): Promise<void> {
      const attemptToken = ++attemptId;
      serverAttemptId = attemptToken;
      generation = disconnectGenerations.get(shipId);
      succeeded = false;
      cancelled = false;
      resolvedAddress = undefined;
      resolveReason = undefined;
      submittingAttempt = undefined;

      if (!nearby) {
        // No unambiguous automatic route. Open the manual form directly -- with no
        // error, since nothing has failed yet.
        manual();
        return;
      }

      emit({ phase: CompanionConnectPhase.connecting });

      try {
        const { timedOut } = await withDeadline(async (finishPairingDeadline) => {
          const resolved = await resolve(shipId).catch(() => undefined);
          if (!isCurrent(attemptToken)) {
            return;
          }
          resolvedAddress = resolved?.address;
          resolveReason = resolved?.reason;
          await attempt(resolvedAddress, attemptToken, finishPairingDeadline);
        }, timeoutMs);
        if (timedOut && isCurrent(attemptToken)) {
          attemptId++;
          manual(CompanionConnectError.timeout);
          cancelExpiredAttempt(attemptToken);
        }
      } catch (error) {
        if (isCurrent(attemptToken)) {
          manual(reasonFor(error));
        }
      }
    },

    async submit(address: string): Promise<void> {
      if (submittingAttempt != undefined) {
        return;
      }
      const attemptToken = ++attemptId;
      submittingAttempt = attemptToken;
      serverAttemptId = attemptToken;
      generation = disconnectGenerations.get(shipId);
      succeeded = false;
      cancelled = false;
      resolvedAddress = address;
      // A typed address stands on its own: a reason left over from the automatic
      // attempt describes a route the user has already abandoned.
      resolveReason = undefined;
      emit({ phase: CompanionConnectPhase.manual, address, submitting: true });
      try {
        // No `.catch()` here, deliberately: a rejection must reach the catch below,
        // which is what turns it into a reason on the form. `attempt` classifies
        // connect failures itself, so what surfaces here is the handoff failing.
        const { timedOut } = await withDeadline(
          (finishPairingDeadline) => attempt(address, attemptToken, finishPairingDeadline), submitTimeoutMs);
        if (!isCurrent(attemptToken)) {
          return;
        }
        if (timedOut) {
          // Supersede the in-flight attempt so its late response cannot commit, exactly
          // as the automatic route does.
          attemptId++;
          manual(CompanionConnectError.timeout);
          cancelExpiredAttempt(attemptToken);
        }
      } catch (error) {
        if (isCurrent(attemptToken)) {
          manual(reasonFor(error));
        }
      } finally {
        // Released even on the deadline path: leaving it set would lock the form for
        // the rest of the dialog's life, turning a timeout into a dead end.
        if (submittingAttempt === attemptToken) {
          submittingAttempt = undefined;
        }
      }
    },

    async cancel(): Promise<void> {
      const token = serverAttemptId;
      attemptId++;
      succeeded = false;
      submittingAttempt = undefined;
      emit({ phase: CompanionConnectPhase.cancelled });
      await cancelServer(token);
      cancelled = true;
    },

    async dispose(): Promise<void> {
      const token = serverAttemptId;
      attemptId++;
      if (!succeeded && !cancelled) {
        await cancelServer(token);
      }
    },
  };
}
